/**
 * RoutingDeskPage — שולחן הניתוב (DIGEMP-MANAGER-ROUTING-DESK-FRONTEND-046).
 *
 * The operator-facing desk that shows everything the manager routing decision is based on BEFORE
 * the operator chooses an employee: the recommendation and its plain-language reasons (from the
 * read-only POST /api/agents/routing-recommendation contract), the candidate employees with their
 * readiness/load, and each candidate's handoff history (from the existing conversation records).
 *
 * The desk itself assigns nothing: no auto-assign, no run start, no message send. The operator's
 * explicit choice is handed to the existing, permission-gated conversation flow, which keeps its
 * own customer-scope and approval checks. Missing evidence is always shown as missing — a failed
 * or absent recommendation source is never replaced by an invented ranking, only by the plain
 * roster of available employees, clearly marked as unranked.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { agentsApi, companiesApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import RoutingCandidatePanel from "../components/RoutingCandidatePanel";
import { agentsForTicket } from "../utils/agentScope";

// The same eligibility rule the backend recommendation applies: enabled and not offline.
const isEligible = (agent) => agent.is_enabled && (agent.availability_status || "available") !== "offline";

export function recommendationToView(candidate) {
  return {
    id: candidate.agent_id,
    name: candidate.name,
    roleTitle: candidate.role_title,
    specialties: candidate.specialties || [],
    availability: candidate.availability,
    effectiveAvailability: candidate.effective_availability,
    workload: candidate.active_workload,
    score: candidate.match_score,
    matchedSpecialties: candidate.matched_specialties || [],
    matchedTerms: candidate.matched_terms || [],
    reasons: candidate.reasons || [],
    avatarKey: null,
    avatarColor: null,
    ranked: true,
  };
}

export function rosterToView(agent) {
  return {
    id: agent.id,
    name: agent.name,
    roleTitle: agent.role_title,
    specialties: agent.specialties || [],
    availability: agent.availability_status || "available",
    effectiveAvailability: null,
    workload: null,
    score: null,
    matchedSpecialties: [],
    matchedTerms: [],
    reasons: [],
    avatarKey: agent.avatar_key,
    avatarColor: agent.avatar_color,
    ranked: false,
  };
}

function availabilityLabel(status) {
  return STATUS_META[status] ? STATUS_META[status].label : status;
}

function CandidateRow({ candidate, isSelected, isRecommended, onSelect }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(candidate.id)}
        aria-pressed={isSelected}
        className={`w-full text-right rounded-lg border px-3 py-2.5 transition-colors ${
          isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center gap-3">
          <EmployeeAvatar
            name={candidate.name}
            avatarKey={candidate.avatarKey}
            avatarColor={candidate.avatarColor}
            status={candidate.effectiveAvailability || candidate.availability}
            showStatus
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-800">{candidate.name}</span>
              {candidate.roleTitle && <span className="text-xs text-gray-500">· {candidate.roleTitle}</span>}
              {isRecommended && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">המלצת המנהל</span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <span>{availabilityLabel(candidate.effectiveAvailability || candidate.availability)}</span>
              {candidate.ranked && candidate.effectiveAvailability && candidate.effectiveAvailability !== candidate.availability && (
                <span className="text-gray-400">(הצהרה: {availabilityLabel(candidate.availability)})</span>
              )}
              {candidate.workload != null && <span>עומס פעיל: {candidate.workload}</span>}
              {candidate.score != null && <span>ניקוד התאמה: {candidate.score}</span>}
            </div>
            {candidate.ranked && (candidate.matchedSpecialties.length > 0 || candidate.matchedTerms.length > 0) && (
              <div className="mt-1 flex flex-wrap gap-1">
                {candidate.matchedSpecialties.map((s) => (
                  <span key={`s-${s}`} className="text-[11px] px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                    התמחות: {s}
                  </span>
                ))}
                {candidate.matchedTerms.slice(0, 4).map((t) => (
                  <span key={`t-${t}`} className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    התאמה: {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

export default function RoutingDeskPage() {
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");
  const canRun = canAgent("agents_run");

  const [request, setRequest] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [submittedRequest, setSubmittedRequest] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
  });
  const { data: companies = [], isError: companiesFailed } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
    enabled: canView,
  });

  const recommendation = useMutation({
    mutationFn: (payload) => agentsApi.routingRecommendation(payload),
  });
  const resetRecommendation = recommendation.reset;
  useEffect(() => {
    // The ranking is customer-scoped: a customer change invalidates the shown recommendation.
    resetRecommendation();
    setSubmittedRequest("");
  }, [customerId, resetRecommendation]);

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בשולחן הניתוב</div>;
  }

  const rec = recommendation.data;
  const recError = recommendation.error;
  const recUnavailable = recommendation.isError && recError?.response?.status === 404;

  // Ranked pool from the recommendation contract; otherwise the plain roster (same eligibility
  // rule; customer-scoped only when a customer is selected, mirroring the backend query) with
  // NO invented ranking.
  const rosterPool = customerId ? agentsForTicket(agents, customerId) : agents;
  const rosterCandidates = rosterPool.filter(isEligible).map(rosterToView);
  const board = rec ? rec.candidates.map(recommendationToView) : rosterCandidates;
  const recommendedId = rec?.recommended?.agent_id || null;
  const selected = board.find((c) => c.id === selectedId) || null;

  const submit = (e) => {
    e.preventDefault();
    const text = request.trim();
    if (!text) return;
    setSubmittedRequest(text);
    recommendation.mutate({ request: text, ...(customerId ? { customer_id: customerId } : {}) });
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-gray-800">שולחן הניתוב</h1>
        <p className="text-xs text-gray-500">הדסק מציג מידע בלבד ואינו מקצה עובדים אוטומטית — ההחלטה נשארת בידי המפעיל.</p>
      </div>

      <form onSubmit={submit} className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">תיאור הפנייה</h2>
        <textarea
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          rows={3}
          placeholder="למשל: הלקוח מדווח שהגיבוי נכשל בשרת הראשי ויש לבדוק את Acronis"
          aria-label="תיאור הפנייה"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-gray-500">
            לקוח (אופציונלי)
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="block mt-1 text-sm border border-gray-200 rounded px-2 py-1.5 min-w-[12rem]"
            >
              <option value="">כל הלקוחות</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          {companiesFailed && (
            <span role="alert" className="text-xs text-red-600">לא ניתן לטעון את רשימת הלקוחות — הסינון לפי לקוח לא זמין כרגע</span>
          )}
          <span className="flex-1" />
          <button
            type="submit"
            disabled={!request.trim() || recommendation.isPending}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {recommendation.isPending ? "מחשב המלצה..." : "חשב המלצת ניתוב"}
          </button>
        </div>
      </form>

      {rec && (
        <section aria-label="המלצת ניתוב" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
          <h2 className="text-sm font-semibold text-gray-700">המלצת ניתוב</h2>
          {submittedRequest && (
            <p className="text-xs text-gray-500">עבור הפנייה: <span className="text-gray-700">{submittedRequest}</span></p>
          )}
          {rec.recommended ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-violet-900">{rec.recommended.name}</span>
                {rec.recommended.role_title && <span className="text-xs text-violet-700">· {rec.recommended.role_title}</span>}
              </div>
              <p className="text-sm text-violet-900">{rec.recommendation_reason}</p>
              {rec.recommended.reasons.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {rec.recommended.reasons.map((r, i) => (
                    <li key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-violet-200 text-violet-800">{r}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">{rec.recommendation_reason}</p>
          )}
          <p className="text-[11px] text-gray-400">
            הדירוג מבוסס על התאמת התמחות (3 נקודות), תפקיד/שם (2) ותיאור (1); זמינות ועומס פעיל מכריעים בשוויון. נשקלו {rec.considered_count} עובדים זמינים.
          </p>
        </section>
      )}

      {recommendation.isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {recUnavailable
            ? "שירות המלצות הניתוב אינו זמין בגרסה הפרוסה — מוצגים העובדים הזמינים ללא דירוג התאמה"
            : "שגיאה בחישוב ההמלצה — מוצגים העובדים הזמינים ללא דירוג התאמה"}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <section aria-label="עובדים מועמדים" className="lg:col-span-2 bg-white rounded-xl border shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">{rec ? "עובדים מדורגים" : "עובדים זמינים"}</h2>
            <span className="text-xs text-gray-400">{board.length} עובדים</span>
          </div>
          {!rec && !recommendation.isError && (
            <p className="text-xs text-gray-400">הזן תיאור פנייה וחשב המלצה כדי לראות דירוג התאמה; אפשר לבחור עובד גם מהרשימה ללא דירוג.</p>
          )}
          {board.length === 0 && (
            <p className="text-sm text-gray-400 italic">
              {rec ? "לא נמצאו עובדים זמינים התואמים את הסינון" : "אין עובדים זמינים כרגע"}
            </p>
          )}
          <ul className="space-y-2">
            {board.map((candidate) => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                isSelected={candidate.id === selectedId}
                isRecommended={candidate.id === recommendedId}
                onSelect={(id) => setSelectedId(id)}
              />
            ))}
          </ul>
        </section>

        <div className="lg:col-span-1">
          {selected ? (
            <RoutingCandidatePanel
              candidate={selected}
              requestText={submittedRequest || request.trim()}
              customerId={customerId}
              canRun={canRun}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <div className="bg-white rounded-xl border shadow-sm p-4 text-sm text-gray-400">
              בחר עובד מהרשימה כדי לראות מצב מוכנות, עומס פעיל והיסטוריית הפניות לפני ההחלטה.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
